import classNames from "classnames"
import React, { useState } from "react"

import { DateTime } from "@bounce/date"
import { Label2 } from "@bounce/design-system"
import { formatStripeAmount, useLocale, useTranslation } from "@bounce/i18n"
import {
  getNextStatusTime,
  isOpenNow,
  parseOpeningHours,
  parseOpeningHoursExceptions,
  resolveStoreTypeIcon,
  useStoreNickname,
} from "@bounce/store"
import { PNImage } from "@bounce/web-components"

import { getPoisWithWalkingTime } from "./helpers"
import type { FeaturedPoiData, StoreSliderProps } from "./StoreSlider"
import { StoreSliderCardPoiItem } from "./StoreSliderCardPoiItem"
import { StoreSliderCardTags } from "./StoreSliderCardTags"
import { StoreSliderCardReview } from "@/components/LuggageStorage/StoreSlider/StoreSliderCardReview"
import { getStoreThumbnail } from "@/libs/bounce/stores/getStoreThumbnail"

const OVERFLOW_STATUS_LANGUAGES = ["de", "en", "ru", "fr"]

type StoreSliderCardProps = {
  store: StoreSliderProps["stores"][number]
  featuredPoi?: FeaturedPoiData
}

export const StoreSliderCard = ({ store, featuredPoi }: StoreSliderCardProps) => {
  const { t, i18n } = useTranslation()
  const { language } = useLocale()
  const [thumbnailError, setThumbnailError] = useState(false)

  const isOpen = isOpenNow(store)
  const statusText = isOpen ? t("cmp.storeSlider.card.open", "Open") : t("cmp.storeSlider.card.closed", "Closed")
  const overflowStatus = OVERFLOW_STATUS_LANGUAGES.includes(language)
  const nextStatusTime = getNextStatusTime({
    currentDate: DateTime.fromObject({}, { zone: store.city.timezone }),
    openingHours: parseOpeningHours(store.openingHours),
    openingHourExceptions: parseOpeningHoursExceptions(store.openingHourExceptions),
    isStoreOpen: isOpen,
    locale: i18n.language,
  })

  const nickname = useStoreNickname({ store, productSuffix: "LUGGAGE_STORAGE" })
  const href = getStoreThumbnail(store)
  const alt = t("cmp.storeSlider.card.storeFrontV1", "Storefront for {NICKNAME}", {
    NICKNAME: nickname,
  })
  const StoreTypeIcon = resolveStoreTypeIcon(store.type)

  const price = formatStripeAmount(store.lowestPrice, store.city.currency, i18n.language)
  const pois = getPoisWithWalkingTime(store, i18n, featuredPoi)

  return (
    <div>
      <div className="size-full h-60 overflow-hidden rounded-lg border border-neutral-300 text-black shadow-2">
        <div className="relative flex size-full">
          <div className="relative h-auto w-20 min-w-20 md:w-32">
            {!!href && !thumbnailError ? (
              <PNImage
                src={href}
                alt={alt}
                className="z-0 size-full rounded-lg object-cover"
                width={128}
                height={196}
                onError={() => setThumbnailError(true)}
              />
            ) : (
              <div className="pointer-events-none absolute inset-0 rounded-lg bg-lilac-100">
                <StoreTypeIcon className="absolute inset-0 m-auto size-8 text-cranberry-700 lg:size-10" />
              </div>
            )}
          </div>

          <div className="flex size-full flex-1 flex-col py-4 pl-3 pr-4">
            <div className="flex size-full flex-col">
              <div
                className={classNames("w-full gap-2", {
                  "absolute inset-x-0 top-4 px-4": overflowStatus,
                })}>
                <StoreSliderCardTags
                  isOpen={isOpen}
                  statusText={statusText}
                  nextStatusTime={nextStatusTime}
                  store={store}
                  overflowStatus={overflowStatus}
                />
              </div>

              <div
                className={classNames("flex flex-col", {
                  "mt-12": overflowStatus,
                  "mt-4": !overflowStatus,
                })}>
                <p className="title6 mb-1 line-clamp-2">{nickname}</p>
                <Label2 className="mb-5 text-neutral-800">
                  {t("cmp.storeSlider.card.priceVariant", "from {PRICE}/bag", { PRICE: price })}
                </Label2>
              </div>
              <ul className="mt-auto w-full space-y-1">
                {pois.map(poi => (
                  <StoreSliderCardPoiItem key={poi.name} {...poi} />
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
      {store.latestReview && <StoreSliderCardReview className="mt-5" review={store.latestReview} />}
    </div>
  )
}
